// Modal-style form para crear gasto. Usa categorías seed (system) por defecto.
// Phase 0: tras crear, opcionalmente sube recibo en una segunda llamada.

import { useState } from 'react';

import {
  useCategories,
  useCreateExpense,
  useUploadReceipt,
} from '../hooks/useExpenses';
import type { Currency, ExpenseStatus } from '../types';

interface Props {
  onCreated?: () => void;
}

export function ExpenseForm({ onCreated }: Props) {
  const { data: categories, loading: catsLoading } = useCategories();
  const { submit, loading: creating, error: createError } = useCreateExpense();
  const { upload, loading: uploading, error: uploadError } = useUploadReceipt();

  const [expenseDate, setExpenseDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState<string>('0.00');
  const [currency, setCurrency] = useState<Currency>('MXN');
  const [description, setDescription] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [categorySlug, setCategorySlug] = useState<string>('software');
  const [projectId, setProjectId] = useState<string>('');
  const [vendorId, setVendorId] = useState<string>('');
  const [status, setStatus] = useState<ExpenseStatus>('recorded');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const reset = () => {
    setAmount('0.00');
    setDescription('');
    setNotes('');
    setReceipt(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);

    const created = await submit({
      expense_date: expenseDate,
      amount,
      currency,
      description: description.trim(),
      notes: notes.trim(),
      category_slug: categorySlug,
      project_id: projectId.trim() || undefined,
      vendor_id: vendorId.trim() || undefined,
      status,
    });
    if (!created) return;

    if (receipt) {
      await upload(created.id, receipt);
    }

    setSuccess(`Gasto #${created.id.slice(0, 8)} registrado.`);
    reset();
    onCreated?.();
  };

  const error = createError || uploadError;
  const busy = creating || uploading;

  return (
    <form onSubmit={onSubmit} className="space-y-3 p-4 border rounded-md max-w-xl">
      <h3 className="text-base font-semibold">Nuevo gasto</h3>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-neutral-600">Fecha</span>
          <input
            type="date"
            required
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-neutral-600">Monto</span>
          <div className="flex gap-1">
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 flex-1 border rounded px-2 py-1 text-sm"
            />
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="mt-1 border rounded px-2 py-1 text-sm"
            >
              <option value="MXN">MXN</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </label>
      </div>

      <label className="block">
        <span className="text-xs text-neutral-600">Descripción</span>
        <input
          type="text"
          required
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="GitHub Copilot, Diseño landing, etc."
          className="mt-1 w-full border rounded px-2 py-1 text-sm"
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-neutral-600">Categoría</span>
          <select
            required
            value={categorySlug}
            onChange={(e) => setCategorySlug(e.target.value)}
            disabled={catsLoading}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          >
            {catsLoading ? (
              <option>Cargando…</option>
            ) : (
              categories.map((c) => (
                <option key={c.id} value={c.slug}>
                  {c.name}
                </option>
              ))
            )}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-neutral-600">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ExpenseStatus)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          >
            <option value="draft">Borrador</option>
            <option value="recorded">Registrado</option>
            <option value="paid">Pagado</option>
            <option value="void">Anulado</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-neutral-600">Proyecto (UUID, opcional)</span>
          <input
            type="text"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm font-mono"
          />
        </label>
        <label className="block">
          <span className="text-xs text-neutral-600">Vendor (Lead UUID, opcional)</span>
          <input
            type="text"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm font-mono"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-xs text-neutral-600">Notas</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full border rounded px-2 py-1 text-sm"
        />
      </label>

      <label className="block">
        <span className="text-xs text-neutral-600">Recibo (opcional, ≤10 MB, image/PDF)</span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          onChange={(e) => setReceipt(e.target.files?.[0] || null)}
          className="mt-1 w-full text-sm"
        />
      </label>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {success && <div className="text-sm text-green-700">{success}</div>}

      <button
        type="submit"
        disabled={busy}
        className="px-4 py-2 text-sm rounded bg-black text-white disabled:opacity-50"
      >
        {busy ? 'Guardando…' : 'Registrar gasto'}
      </button>
    </form>
  );
}
